import { byShop, getProjectId } from "../0_global/shop_map";

const PRODUCT_SETS_URLS = {
	cs: "https://cdn.myshoptet.com/usr/www.natios.cz/user/documents/upload/shoptet_uploads/product_sets/product_sets.json",
	sk: "https://cdn.myshoptet.com/usr/www.natios.sk/user/documents/upload/shoptet_uploads/product_sets/product_sets.json",
};
const PRODUCT_SETS_CACHE_KEY = "natiosProductSets";

export async function initProductSets() {
	if (!document.body.classList.contains("type-product")) return null;

	const productGuid = getCurrentProductGuid();
	if (!productGuid) {
		console.warn("[NAT] product_sets: product guid not found in dataLayer");
		return null;
	}

	const data = await loadProductSets();
	if (!data?.sets) {
		console.warn("[NAT] product_sets: JSON not loaded");
		return null;
	}

	const setData = findSetByGuid(data.sets, productGuid);
	if (!setData?.products?.length) {
		console.warn("[NAT] product_sets: no set for guid", productGuid);
		return null;
	}

	const products = setData.products.filter((product) => product?.visible !== false);
	if (!products.length) return null;

	return insertProductSetWidget(products);
}

function getCurrentProductGuid() {
	try {
		if (typeof getShoptetDataLayer === "function") {
			const product = getShoptetDataLayer("product") || getShoptetDataLayer()?.product;
			if (product?.guid) return String(product.guid).trim();
		}
	} catch (_) {}

	try {
		const fromDataLayer = dataLayer?.[0]?.shoptet?.product?.guid;
		if (fromDataLayer) return String(fromDataLayer).trim();
	} catch (_) {}

	return "";
}

function findSetByGuid(sets, productGuid) {
	const guid = String(productGuid).toLowerCase();
	return Object.values(sets).find((set) => String(set?.guid || "").toLowerCase() === guid) || null;
}

async function loadProductSets() {
	const cacheKey = PRODUCT_SETS_CACHE_KEY + (getProjectId() || location.hostname);
	const url = byShop(PRODUCT_SETS_URLS);

	try {
		const cached = sessionStorage.getItem(cacheKey);
		if (cached) {
			return JSON.parse(cached);
		}
	} catch (_) {}

	try {
		const response = await fetch(url + "?_=" + Date.now());
		if (!response.ok) {
			console.warn("[NAT] product_sets: fetch failed", response.status, url);
			return null;
		}
		const data = await response.json();
		try {
			sessionStorage.setItem(cacheKey, JSON.stringify(data));
		} catch (_) {}
		return data;
	} catch (error) {
		console.warn("[NAT] product_sets: failed to load JSON", error);
		return null;
	}
}

async function insertProductSetWidget(products) {
	const existing = document.getElementById("product-set");
	if (existing) return existing;

	const tabContent = document.querySelector("#tab-content");
	if (!tabContent) {
		console.warn("[NAT] product_sets: #tab-content not found");
		return null;
	}

	if (typeof window.NAT?.utils?.downloadAndSaveMeasureUnitFilter === "function") {
		await window.NAT.utils.downloadAndSaveMeasureUnitFilter();
	}

	const widget = document.createElement("div");
	widget.id = "product-set";
	widget.className = "product-widget product-set-widget";
	widget.setAttribute("widget-type", "product-set");

	const title = window.NAT.translations("productSetTitle");
	const cardsHtml = products.map(renderProductSetCard).join("");

	widget.innerHTML = `
		<div class="widget-content">
			<h2 class="product-set-widget__title"><strong>${title}</strong></h2>
			<div class="product-set-grid">${cardsHtml}</div>
		</div>
	`;

	tabContent.prepend(widget);

	return widget;
}

function renderProductSetCard(product) {
	const name = escapeHtml(product.name || "");
	const desc = escapeHtml(product.shortDescription || "");
	const url = escapeHtml(product.url || "#");
	const image = escapeHtml(product.image || "");
	const badge = escapeHtml(getProductSetBadge(product));
	const badgeHtml = badge ? `<span class="product-set-card__badge">${badge}</span>` : "";

	return `
		<a class="product-set-card" href="${url}">
			<div class="product-set-card__image">
				${image ? `<img src="${image}" alt="${name}" loading="lazy" width="246" height="235">` : ""}
				${badgeHtml}
			</div>
			<div class="product-set-card__info">
				<span class="product-set-card__name">${name}</span>
				${desc ? `<span class="product-set-card__desc">${desc}</span>` : ""}
			</div>
		</a>
	`;
}

function measureLabelForCode(code) {
	const filters = window.NAT.measureFiltersData;
	if (!Array.isArray(filters) || !code) return "";
	const wanted = String(code).trim();
	const match = filters.find((item) => String(item?.code ?? "").trim() === wanted);
	if (!match?.value) return "";
	return String(match.value)
		.replace(/\s+/g, " ")
		.trim()
		.replace(/(\d)([^\d\s])/, "$1 $2");
}

function getProductSetBadge(product) {
	const amount = Number(product.amount);
	const hasAmount = Number.isFinite(amount) && amount > 0;
	const measure = measureLabelForCode(product.code);
	if (hasAmount && measure) {
		return `${amount} × ${measure}`;
	}
	if (measure) return measure;
	if (hasAmount) return `${amount}×`;
	return "";
}

function escapeHtml(value) {
	return String(value)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}
